import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Wallet, Calculator, MapPin, FloppyDisk, EnvelopeSimple, PenNib,
  UploadSimple, Trash, SpinnerGap, CaretDown, Users,
} from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AppConfigSections — full editor for the unified app config.
 *
 * IMPORTANT: helper components (NumField/TxtField/SaveBtn) live OUTSIDE the
 * render scope so React doesn't recreate them on every keystroke. That's the
 * bug that made inputs lose focus after each character.
 */

const NumField = ({ value, onChange, label, hint, min = 0, max = 9999, step = 1, testId }) => (
  <div className="space-y-1">
    <Label className="text-xs text-start block">{label}</Label>
    <Input
      type="number" min={min} max={max} step={step}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
      className="text-center font-bold text-[#655dc6]"
      dir="ltr"
      data-testid={testId}
    />
    {hint && <p className="text-[10px] text-[#655dc6]/50">{hint}</p>}
  </div>
);

const TxtField = ({ value, onChange, label, hint, dir = 'rtl', ltr = false, testId }) => (
  <div className="space-y-1">
    <Label className="text-xs text-start block">{label}</Label>
    <Input
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      dir={ltr ? 'ltr' : dir}
      className={ltr ? 'text-left' : ''}
      data-testid={testId}
    />
    {hint && <p className="text-[10px] text-[#655dc6]/50">{hint}</p>}
  </div>
);

const SaveBtn = ({ onClick, saving, isAr, testId }) => (
  <div className="flex justify-end pt-2">
    <Button
      onClick={onClick}
      disabled={saving}
      className="bg-[#655dc6] hover:bg-[#544cb0] text-white gap-2"
      data-testid={testId}
    >
      <FloppyDisk size={16} weight="duotone" />
      {saving ? (isAr ? 'جاري الحفظ...' : 'Saving...') : (isAr ? 'حفظ' : 'Save')}
    </Button>
  </div>
);

export default function AppConfigSections({ language, direction }) {
  const isAr = language === 'ar';
  const [cfg, setCfg] = useState(null);
  const [savingKey, setSavingKey] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/settings/config`, { withCredentials: true });
        setCfg(data.values);
      } catch (e) {
        console.error('config fetch failed', e);
      }
    })();
  }, []);

  const update = (k, v) => setCfg((p) => ({ ...p, [k]: v }));

  const saveSection = async (sectionId, keys) => {
    if (!cfg) return;
    setSavingKey(sectionId);
    try {
      const payload = Object.fromEntries(keys.map((k) => [k, cfg[k]]));
      const { data } = await axios.put(`${API}/settings/config`, payload, { withCredentials: true });
      setCfg(data.values);
      toast.success(isAr ? 'تم حفظ الإعدادات' : 'Settings saved');
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || (isAr ? 'فشل الحفظ' : 'Save failed'));
    } finally {
      setSavingKey(null);
    }
  };

  if (!cfg) {
    return (
      <Card className="border-[#C4BFF0]"><CardContent className="py-10 text-center text-[#655dc6]/60">
        {isAr ? 'جاري التحميل...' : 'Loading...'}
      </CardContent></Card>
    );
  }

  return (
    <div
      className="grid grid-cols-1 lg:grid-cols-2 gap-6"
      data-testid="settings-grid"
    >
      {/* 1. Payroll cycle */}
      <Card className="border-[#C4BFF0]" data-testid="payroll-cycle-card">
        <CardHeader>
          <CardTitle className="text-[#655dc6] flex items-center gap-2">
            <Wallet size={22} weight="duotone" />
            {isAr ? 'دورة صرف الرواتب' : 'Payroll Cycle'}
          </CardTitle>
          <p className="text-xs text-[#655dc6]/60 mt-1">
            {isAr
              ? 'النطاق الذي يتم فيه إصدار وصرف رواتب الشهر — يظهر كتذكير في صفحة الرواتب'
              : 'The window each month during which payroll is issued and disbursed — shown as a banner on the Payroll page.'}
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-3 max-w-md">
            <NumField
              value={cfg.payroll_cycle_start_day}
              onChange={(v) => update('payroll_cycle_start_day', v)}
              label={isAr ? 'يبدأ من يوم' : 'Start day'} min={1} max={31}
              testId="cfg-payroll-cycle-start-day"
            />
            <NumField
              value={cfg.payroll_cycle_end_day}
              onChange={(v) => update('payroll_cycle_end_day', v)}
              label={isAr ? 'حتى يوم' : 'End day'} min={1} max={31}
              testId="cfg-payroll-cycle-end-day"
            />
          </div>
          <p className="text-[11px] bg-[#E8E6F5] text-[#655dc6] px-3 py-2 rounded-md">
            {isAr
              ? (cfg.payroll_cycle_start_day === cfg.payroll_cycle_end_day
                  ? `يُصدر الراتب يوم ${cfg.payroll_cycle_start_day} من كل شهر.`
                  : `الدورة الحالية: من يوم ${cfg.payroll_cycle_start_day} إلى يوم ${cfg.payroll_cycle_end_day} من كل شهر.`)
              : (cfg.payroll_cycle_start_day === cfg.payroll_cycle_end_day
                  ? `Payroll is issued on day ${cfg.payroll_cycle_start_day} of each month.`
                  : `Current cycle: from day ${cfg.payroll_cycle_start_day} to day ${cfg.payroll_cycle_end_day} of each month.`)}
          </p>
          <SaveBtn isAr={isAr} saving={savingKey === 'payroll'}
                   onClick={() => saveSection('payroll', ['payroll_cycle_start_day', 'payroll_cycle_end_day'])}
                   testId="save-payroll" />
        </CardContent>
      </Card>

      {/* 2. Calculation knobs */}
      <Card className="border-[#C4BFF0]" data-testid="calc-rules-card">
        <CardHeader>
          <CardTitle className="text-[#655dc6] flex items-center gap-2">
            <Calculator size={22} weight="duotone" />
            {isAr ? 'قواعد احتساب الأجور والإجازات' : 'Wage & Leave Rules'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <NumField value={cfg.daily_wage_divisor} onChange={(v) => update('daily_wage_divisor', v)}
              label={isAr ? 'مقسوم الأجر اليومي' : 'Daily wage divisor'}
              hint={isAr ? 'افتراضي ٣٠ — يستخدم في بدل الإجازة، نهاية الخدمة، الجزاءات' : 'Default 30 — used in leave allowance, EOS, penalties'}
              min={20} max={31} testId="cfg-daily-wage-divisor" />
            <NumField value={cfg.annual_leave_days} onChange={(v) => update('annual_leave_days', v)}
              label={isAr ? 'الإجازة السنوية الأساسية (أيام)' : 'Annual leave (days)'} min={1} max={60}
              testId="cfg-annual-leave-days" />
            <NumField value={cfg.overtime_multiplier} onChange={(v) => update('overtime_multiplier', v)}
              label={isAr ? 'معامل ساعة الإضافي' : 'Overtime multiplier'} min={1} max={5} step={0.1}
              testId="cfg-overtime-multiplier" />
            <NumField value={cfg.eos_first_5_years_days} onChange={(v) => update('eos_first_5_years_days', v)}
              label={isAr ? 'مكافأة أول ٥ سنوات (يوم/سنة)' : 'EOS first 5y (days/year)'} min={0} max={60}
              testId="cfg-eos-first-5-years-days" />
            <NumField value={cfg.eos_after_5_years_days} onChange={(v) => update('eos_after_5_years_days', v)}
              label={isAr ? 'مكافأة ما بعد ٥ سنوات (يوم/سنة)' : 'EOS after 5y (days/year)'} min={0} max={60}
              testId="cfg-eos-after-5-years-days" />
          </div>

          {/* Tiered annual leave — toggle + linked field in one block */}
          <div className="rounded-md bg-[#F2F0FA] border border-[#C4BFF0] p-3 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div className="flex-1">
                <Label className="text-xs font-semibold text-[#655dc6]">
                  {isAr ? 'تفعيل الإجازة المتدرّجة (٢١ يوم لأقل من ٥ سنوات / ٣٠ بعدها)' : 'Use tiered annual leave (21 days <5y / 30 after)'}
                </Label>
                <p className="text-[10px] text-[#655dc6]/60 mt-0.5">
                  {isAr
                    ? 'عند التفعيل: يستخدم النظام "إجازة سنوية لأقل من ٥ سنوات" للموظفين الجدد، والقيمة الأساسية للقدامى.'
                    : 'When enabled, the system uses the <5y value for new hires and the base value for senior employees.'}
                </p>
              </div>
              <Switch
                checked={!!cfg.use_tiered_annual_leave}
                onCheckedChange={(v) => {
                  // When turning ON, auto-set under-5y to Saudi default 21 if it equals
                  // the base annual_leave_days (i.e. user never customised it).
                  setCfg((p) => {
                    const next = { ...p, use_tiered_annual_leave: v };
                    if (v) {
                      const u5 = Number(p.annual_leave_days_under_5y) || 0;
                      const base = Number(p.annual_leave_days) || 0;
                      if (!u5 || u5 === base) next.annual_leave_days_under_5y = 21;
                    }
                    return next;
                  });
                }}
                data-testid="cfg-use-tiered-annual-leave"
              />
            </div>

            {/* Under-5y field — visually disabled when toggle is OFF */}
            <div className={cfg.use_tiered_annual_leave ? '' : 'opacity-50 pointer-events-none'}>
              <NumField
                value={cfg.annual_leave_days_under_5y}
                onChange={(v) => update('annual_leave_days_under_5y', v)}
                label={isAr ? 'إجازة سنوية لأقل من ٥ سنوات (يوم)' : 'Annual leave for <5 years (days)'}
                hint={
                  cfg.use_tiered_annual_leave
                    ? (isAr ? 'سيتم تطبيق هذه القيمة على الموظفين الذين خدمتهم أقل من ٥ سنوات.' : 'Applied to employees with <5 years of service.')
                    : (isAr ? 'يُفعّل من الزر أعلاه — القيمة الافتراضية ٢١ يوم.' : 'Enable the switch above first — default 21 days.')
                }
                min={1} max={60}
                testId="cfg-annual-leave-days-under-5y"
              />
            </div>
          </div>

          <SaveBtn isAr={isAr} saving={savingKey === 'calc'}
            onClick={() => saveSection('calc', [
              'daily_wage_divisor', 'annual_leave_days', 'annual_leave_days_under_5y',
              'use_tiered_annual_leave', 'overtime_multiplier',
              'eos_first_5_years_days', 'eos_after_5_years_days',
            ])} testId="save-calc" />
        </CardContent>
      </Card>

      {/* 3. Geofence */}
      <Card className="border-[#C4BFF0]" data-testid="geofence-card">
        <CardHeader>
          <CardTitle className="text-[#655dc6] flex items-center gap-2">
            <MapPin size={22} weight="duotone" />
            {isAr ? 'النطاق الجغرافي للعيادة (Geofence)' : 'Clinic Geofence'}
          </CardTitle>
          <p className="text-xs text-[#655dc6]/60 mt-1">
            {isAr
              ? 'الموظف يجب أن يكون داخل هذا النطاق ليتمكن من تسجيل الحضور / الانصراف.'
              : 'Employees must be within this radius to be able to clock in/out.'}
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <NumField value={cfg.geofence_lat} onChange={(v) => update('geofence_lat', v)}
              label={isAr ? 'خط العرض (Latitude)' : 'Latitude'} step={0.000001} min={-90} max={90}
              testId="cfg-geofence-lat" />
            <NumField value={cfg.geofence_lon} onChange={(v) => update('geofence_lon', v)}
              label={isAr ? 'خط الطول (Longitude)' : 'Longitude'} step={0.000001} min={-180} max={180}
              testId="cfg-geofence-lon" />
            <NumField value={cfg.geofence_radius_km} onChange={(v) => update('geofence_radius_km', v)}
              label={isAr ? 'نصف القطر (كم)' : 'Radius (km)'} step={0.1} min={0} max={50}
              testId="cfg-geofence-radius-km" />
          </div>
          <a
            href={`https://www.google.com/maps?q=${cfg.geofence_lat},${cfg.geofence_lon}`}
            target="_blank" rel="noopener noreferrer"
            className="text-xs text-[#655dc6] underline hover:text-[#544cb0]"
          >
            {isAr ? 'عرض الموقع على خرائط Google' : 'View on Google Maps'}
          </a>
          <SaveBtn isAr={isAr} saving={savingKey === 'geofence'}
            onClick={() => saveSection('geofence', ['geofence_lat', 'geofence_lon', 'geofence_radius_km'])}
            testId="save-geofence" />
        </CardContent>
      </Card>

      {/* 4. Attendance Tracking Start Date — one-time roll-out grace */}
      <Card className="border-amber-200 bg-amber-50/30" data-testid="tracking-start-card">
        <CardHeader>
          <CardTitle className="text-amber-800 flex items-center gap-2">
            <MapPin size={22} weight="duotone" />
            {isAr ? 'تاريخ بداية تتبّع الحضور (للتدشين)' : 'Attendance Tracking Start Date (Roll-out)'}
          </CardTitle>
          <p className="text-xs text-amber-700/80 mt-1 leading-relaxed">
            {isAr
              ? 'يُستخدم مرة واحدة عند بدء استخدام النظام. كل التواريخ السابقة لهذا التاريخ لن تُحسب غياباً للموظفين. اتركه فارغاً بعد التدشين.'
              : 'One-time setting used during system roll-out. Any date BEFORE this value is excluded from absence calculations. Leave empty in steady state.'}
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
            <div className="space-y-1">
              <label className="text-xs font-medium text-amber-900">
                {isAr ? 'التاريخ' : 'Start Date'}
              </label>
              <input
                type="date"
                value={cfg.attendance_tracking_start_date || ''}
                onChange={(e) => update('attendance_tracking_start_date', e.target.value)}
                dir="ltr"
                className="w-full h-10 px-3 rounded-md border border-amber-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                data-testid="cfg-tracking-start-date"
              />
              {!!cfg.attendance_tracking_start_date && (
                <button
                  type="button"
                  onClick={() => update('attendance_tracking_start_date', '')}
                  className="text-[11px] text-amber-700 hover:text-amber-900 underline"
                  data-testid="clear-tracking-start"
                >
                  {isAr ? '✕ مسح التاريخ' : '✕ Clear date'}
                </button>
              )}
            </div>
          </div>
          <SaveBtn
            isAr={isAr}
            saving={savingKey === 'tracking_start'}
            onClick={() => saveSection('tracking_start', ['attendance_tracking_start_date'])}
            testId="save-tracking-start"
          />
        </CardContent>
      </Card>

      {/* ── IT Manager (routes id_card requests) ─────────── */}
      <Card className="border-[#C4BFF0]">
        <CardHeader>
          <CardTitle className="text-[#655dc6] flex items-center gap-2">
            <EnvelopeSimple size={18} weight="duotone" />
            {isAr ? 'مدير تقنية المعلومات' : 'IT Manager'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-[#655dc6]/70 leading-relaxed">
            {isAr
              ? 'اختر الموظف الذي يعمل مدير تقنية المعلومات. سيستلم إشعاراً وبريداً عند اعتماد أي طلب بطاقة تعريف وتحويله إليه، وستظهر الطلبات في صفحة "طلبات القسم" على لوحته الشخصية.'
              : 'Choose the employee who acts as IT Manager. They receive an in-app notification and an email whenever an ID-card request is approved and forwarded, and see it under "Department Requests" on their home.'}
          </p>
          <ITManagerPicker
            isAr={isAr}
            currentEmail={cfg.it_manager_email}
            onChange={(email) => update('it_manager_email', email)}
          />
          <SaveBtn
            isAr={isAr}
            saving={savingKey === 'it_manager'}
            onClick={() => saveSection('it_manager', ['it_manager_email'])}
            testId="save-it-manager"
          />
        </CardContent>
      </Card>

      {/* ── Finance Manager signature ───────────────────────────── */}
      <FinanceSignatureCard
        isAr={isAr}
        cfg={cfg}
        update={update}
        saveSection={saveSection}
        savingKey={savingKey}
      />

      {/* ── Manager permissions ─────────────────────────────────── */}
      <Card className="border-[#C4BFF0]" data-testid="dept-manager-permissions-card">
        <CardHeader>
          <CardTitle className="text-[#655dc6] flex items-center gap-2">
            {isAr ? 'صلاحيات مدير القسم' : 'Department Manager Permissions'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <Label className="text-sm text-[#655dc6] font-medium">
                {isAr
                  ? 'تفعيل ميزة عرض حضور موظفي القسم (مفتاح رئيسي)'
                  : 'Enable team-attendance feature (master switch)'}
              </Label>
              <p className="text-xs text-[#655dc6]/70 mt-1">
                {isAr
                  ? 'عند التفعيل، تصبح الميزة متاحة، ولكن كل مدير يحتاج أيضاً إلى منح صلاحية فردية في القائمة أدناه.'
                  : 'When enabled, the feature becomes available. Each manager must still be granted individually in the list below.'}
              </p>
            </div>
            <Switch
              checked={!!cfg.dept_manager_can_view_attendance}
              onCheckedChange={(v) => update('dept_manager_can_view_attendance', v)}
              data-testid="dept-mgr-view-attendance-toggle"
            />
          </div>
          <SaveBtn
            isAr={isAr}
            saving={savingKey === 'mgr_perms'}
            onClick={() => saveSection('mgr_perms', ['dept_manager_can_view_attendance'])}
            testId="save-mgr-perms"
          />

          <ManagerPermissionsGrid
            isAr={isAr}
            enabledGlobally={!!cfg.dept_manager_can_view_attendance}
          />
        </CardContent>
      </Card>

    </div>
  );
}

/**
 * FinanceSignatureCard — uploads a PNG/JPG signature to the server and
 * persists the resulting URL in `finance_signature_url`. The name shown
 * next to the signature on clearance PDFs is editable independently.
 */
function FinanceSignatureCard({ isAr, cfg, update, saveSection, savingKey }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = React.useRef(null);
  const url = cfg?.finance_signature_url || '';

  const handleUpload = async (file) => {
    if (!file) return;
    if (!/\.(png|jpe?g)$/i.test(file.name)) {
      toast.error(isAr ? 'يجب أن يكون الملف PNG أو JPG' : 'File must be PNG or JPG');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error(isAr ? 'حجم الملف يجب ألا يتجاوز 2 ميجابايت' : 'File must not exceed 2 MB');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API}/settings/finance-signature`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      update('finance_signature_url', data.url);
      toast.success(isAr ? 'تم رفع التوقيع' : 'Signature uploaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || (isAr ? 'فشل الرفع' : 'Upload failed'));
    } finally {
      setUploading(false);
    }
  };

  const handleClear = async () => {
    if (!window.confirm(isAr
      ? 'سيتم إزالة توقيع المدير المالي من كل المخالصات القادمة. متابعة؟'
      : 'The finance-manager signature will be removed from all future clearances. Continue?'
    )) return;
    try {
      await axios.delete(`${API}/settings/finance-signature`, { withCredentials: true });
      update('finance_signature_url', '');
      toast.success(isAr ? 'تمت إزالة التوقيع' : 'Signature cleared');
    } catch (e) {
      toast.error(e.response?.data?.detail || (isAr ? 'فشل الحذف' : 'Clear failed'));
    }
  };

  return (
    <Card className="border-[#C4BFF0]" data-testid="finance-signature-card">
      <CardHeader>
        <CardTitle className="text-[#655dc6] flex items-center gap-2">
          <PenNib size={18} weight="duotone" />
          {isAr ? 'توقيع المدير المالي' : 'Finance Manager Signature'}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-[#655dc6]/70 leading-relaxed">
          {isAr
            ? 'يتم إضافة صورة التوقيع تلقائياً على المخالصات النقدية وبدل الإجازات المعتمدة (خانة توقيع صاحب العمل). يُفضّل استخدام صورة PNG بخلفية شفافة.'
            : 'The signature image is automatically stamped on approved cash-only and leave-allowance clearance PDFs (Employer Signature slot). A transparent-background PNG is recommended.'}
        </p>

        <div className="flex items-start gap-4">
          {url ? (
            <img
              src={`${process.env.REACT_APP_BACKEND_URL}${url}`}
              alt="finance signature"
              className="max-w-[220px] max-h-[80px] border border-[#C4BFF0] rounded-md bg-white p-2 object-contain"
              data-testid="finance-signature-preview"
            />
          ) : (
            <div className="w-[220px] h-[80px] border border-dashed border-[#C4BFF0] rounded-md flex items-center justify-center text-[10px] text-[#655dc6]/50" data-testid="finance-signature-placeholder">
              {isAr ? 'لا يوجد توقيع مرفوع' : 'No signature uploaded'}
            </div>
          )}
          <div className="flex flex-col gap-2">
            <input
              ref={inputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg"
              className="hidden"
              onChange={(e) => handleUpload(e.target.files?.[0])}
              data-testid="finance-signature-file-input"
            />
            <Button
              type="button" variant="outline"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="border-[#C4BFF0] text-[#655dc6] gap-2"
              data-testid="finance-signature-upload-btn"
            >
              {uploading ? <SpinnerGap size={14} className="animate-spin" /> : <UploadSimple size={14} />}
              {url ? (isAr ? 'استبدال التوقيع' : 'Replace signature') : (isAr ? 'رفع توقيع' : 'Upload signature')}
            </Button>
            {url && (
              <Button
                type="button" variant="ghost"
                onClick={handleClear}
                className="text-red-600 hover:bg-red-50 gap-2"
                data-testid="finance-signature-clear-btn"
              >
                <Trash size={14} />
                {isAr ? 'إزالة التوقيع' : 'Remove signature'}
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <TxtField
            value={cfg.finance_signature_name_ar}
            onChange={(v) => update('finance_signature_name_ar', v)}
            label={isAr ? 'الاسم بجانب التوقيع (عربي)' : 'Name (Arabic)'}
            hint={isAr ? 'اختياري — سيُستخدم اسم المُراجِع إن ترك فارغاً' : 'Optional — falls back to the reviewer name'}
            testId="cfg-fin-sig-name-ar"
          />
          <TxtField
            value={cfg.finance_signature_name_en}
            onChange={(v) => update('finance_signature_name_en', v)}
            label={isAr ? 'الاسم بجانب التوقيع (إنجليزي)' : 'Name (English)'}
            hint={isAr ? 'اختياري' : 'Optional'}
            ltr
            testId="cfg-fin-sig-name-en"
          />
        </div>

        <SaveBtn
          isAr={isAr}
          saving={savingKey === 'fin_sig'}
          onClick={() => saveSection('fin_sig', ['finance_signature_name_ar', 'finance_signature_name_en'])}
          testId="save-fin-sig"
        />
      </CardContent>
    </Card>
  );
}

/**
 * ITManagerPicker — searchable employee selector. The chosen employee's
 * email is written back into `it_manager_email` in app_config. Backend
 * derivation of `is_it_manager` (in auth.py) works unchanged because
 * everything downstream still keys off the email string.
 */
function ITManagerPicker({ isAr, currentEmail, onChange }) {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/employees`, { withCredentials: true });
        setEmployees(Array.isArray(data) ? data : []);
      } catch {
        setEmployees([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const options = employees
    .filter((e) => e && e.role !== 'admin' && !e.terminated_at)
    .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'ar'));

  const selected = options.find(
    (e) => (e.email || '').toLowerCase() === (currentEmail || '').toLowerCase(),
  );

  const filtered = React.useMemo(() => {
    const q = (search || '').toLowerCase().trim();
    if (!q) return options;
    return options.filter((e) => {
      const num = String(e.employee_number || '').toLowerCase();
      const nm = String(e.name || '').toLowerCase();
      const email = String(e.email || '').toLowerCase();
      return num.includes(q) || nm.includes(q) || email.includes(q);
    });
  }, [options, search]);

  return (
    <div className="space-y-2" data-testid="it-manager-picker">
      <Label className="text-xs text-start block">
        {isAr ? 'اختر الموظف' : 'Choose employee'}
      </Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="w-full flex items-center justify-between gap-2 h-10 px-3 rounded-md border border-[#C4BFF0] bg-white text-sm text-start hover:border-[#655dc6] transition-colors"
            data-testid="it-manager-trigger"
          >
            {selected ? (
              <span className="flex items-center gap-2 truncate">
                {selected.employee_number && (
                  <span className="font-mono text-xs text-[#655dc6]/70 tabular-nums">
                    {selected.employee_number}
                  </span>
                )}
                <span className="truncate">{selected.name}</span>
                <span className="text-[10px] text-[#655dc6]/50">·</span>
                <span dir="ltr" className="text-[10px] text-[#655dc6]/60 truncate">
                  {selected.email}
                </span>
              </span>
            ) : (
              <span className="text-[#655dc6]/60">
                {loading
                  ? (isAr ? 'جاري التحميل…' : 'Loading…')
                  : currentEmail
                    ? (isAr ? `⚠︎ لا يوجد موظف بهذا البريد: ${currentEmail}` : `⚠︎ No employee matches: ${currentEmail}`)
                    : (isAr ? 'اختر موظفاً…' : 'Choose an employee…')}
              </span>
            )}
            <CaretDown size={14} weight="bold" className="text-[#655dc6]/60 shrink-0" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="p-0 w-[--radix-popover-trigger-width] max-h-[340px]" align="start">
          <div className="p-2 border-b border-[#C4BFF0]">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={isAr ? 'الاسم أو الرقم الوظيفي أو البريد…' : 'Name, number or email…'}
              className="h-8 bg-white border-[#C4BFF0]"
              autoFocus
              data-testid="it-manager-search"
            />
          </div>
          <div className="max-h-[260px] overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-6 text-center text-sm text-[#655dc6]/60">
                {isAr ? 'لا يوجد موظف مطابق.' : 'No employee found.'}
              </div>
            ) : filtered.map((e) => (
              <button
                key={e.id}
                type="button"
                onClick={() => {
                  onChange((e.email || '').toLowerCase());
                  setOpen(false);
                  setSearch('');
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-start hover:bg-[#F2F0FA] transition-colors"
                data-testid={`it-manager-option-${e.id}`}
              >
                <span className="font-mono text-xs text-[#655dc6]/70 tabular-nums w-14 shrink-0">
                  {e.employee_number || '—'}
                </span>
                <span className="flex-1 truncate text-[#2D2A5C]">{e.name}</span>
                <span dir="ltr" className="text-[10px] text-[#655dc6]/50 truncate max-w-[160px]">
                  {e.email}
                </span>
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
      {selected && (
        <p className="text-[11px] text-emerald-700 flex items-center gap-1">
          <span>✓</span>
          <span dir="ltr">{selected.email}</span>
          <span className="text-[#655dc6]/40">·</span>
          <span>{isAr ? 'سيستلم البريد والإشعارات' : 'will receive emails & alerts'}</span>
        </p>
      )}
    </div>
  );
}


// ── Per-manager toggle grid ────────────────────────────────────────────
// Lists every user with at least one direct report + a Switch that
// grants/revokes the "view team attendance" permission individually.
// Only meaningful when the global feature toggle is ON — visually dims
// itself with a subtle overlay otherwise.
function ManagerPermissionsGrid({ isAr, enabledGlobally }) {
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/attendance/team/managers`, { withCredentials: true });
        setManagers(data?.managers || []);
      } catch (e) {
        // Non-fatal — the section just stays empty if the fetch fails.
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggle = async (m, next) => {
    setSavingId(m.id);
    try {
      await axios.patch(`${API}/attendance/team/managers/${m.id}`, {
        can_view_team_attendance: next,
      }, { withCredentials: true });
      setManagers((prev) => prev.map((x) => x.id === m.id ? { ...x, can_view_team_attendance: next } : x));
      toast.success(isAr
        ? (next ? `تم منح ${m.name} صلاحية العرض` : `تم إلغاء صلاحية ${m.name}`)
        : (next ? `Granted ${m.name}` : `Revoked ${m.name}`));
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isAr ? 'فشل التحديث' : 'Update failed'));
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="pt-2 border-t border-[#C4BFF0]/50">
      <div className="flex items-center gap-2 mb-3">
        <Users size={18} weight="duotone" className="text-[#655dc6]" />
        <h4 className="text-sm font-semibold text-[#655dc6]">
          {isAr ? 'قائمة المديرين — تفعيل فردي' : 'Managers — per-user grant'}
        </h4>
      </div>
      {loading ? (
        <p className="text-xs text-[#655dc6]/60">{isAr ? 'جارٍ التحميل…' : 'Loading…'}</p>
      ) : managers.length === 0 ? (
        <p className="text-xs text-[#655dc6]/60">
          {isAr ? 'لا يوجد مديرون بعد. عيّن مديراً لأحد الموظفين من ملفه الشخصي أولاً.' : 'No managers yet. Assign a manager to at least one employee first.'}
        </p>
      ) : (
        <div className={`max-h-[360px] overflow-y-auto rounded-lg border border-[#C4BFF0] ${!enabledGlobally ? 'opacity-50 pointer-events-none' : ''}`}
             data-testid="mgr-perm-grid">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-[#F2F0FA] z-10">
              <tr>
                <th className="text-start px-3 py-2 text-xs text-[#655dc6]">{isAr ? 'المدير' : 'Manager'}</th>
                <th className="text-center px-3 py-2 text-xs text-[#655dc6]">{isAr ? 'القسم' : 'Department'}</th>
                <th className="text-center px-3 py-2 text-xs text-[#655dc6]">{isAr ? 'عدد المرؤوسين' : 'Reports'}</th>
                <th className="text-center px-3 py-2 text-xs text-[#655dc6]">{isAr ? 'الصلاحية' : 'Permission'}</th>
              </tr>
            </thead>
            <tbody>
              {managers.map((m) => (
                <tr key={m.id} className="border-t border-[#C4BFF0]/40 hover:bg-[#F2F0FA]/50" data-testid={`mgr-perm-row-${m.id}`}>
                  <td className="px-3 py-2 text-[#655dc6]">
                    <div className="font-medium">{isAr ? m.name : (m.name_en || m.name)}</div>
                    <div className="text-[10px] text-[#655dc6]/60" dir="ltr">{m.email}</div>
                  </td>
                  <td className="px-3 py-2 text-center text-xs text-[#655dc6]/80">{m.department || '—'}</td>
                  <td className="px-3 py-2 text-center text-xs">
                    <span className="inline-block bg-[#E8E6F5] text-[#655dc6] px-2 py-0.5 rounded-full">
                      {m.reports_count}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-center">
                    {savingId === m.id ? (
                      <SpinnerGap size={16} className="animate-spin inline-block text-[#655dc6]" />
                    ) : (
                      <Switch
                        checked={!!m.can_view_team_attendance}
                        onCheckedChange={(v) => toggle(m, v)}
                        data-testid={`mgr-perm-switch-${m.id}`}
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {!enabledGlobally && managers.length > 0 && (
        <p className="text-[10px] text-amber-700 mt-2">
          {isAr ? '⚠️ فعّل المفتاح الرئيسي أعلاه أولاً لتصبح الصلاحيات الفردية نافذة.' : '⚠️ Enable the master switch above first for these individual grants to take effect.'}
        </p>
      )}
    </div>
  );
}
